import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, BookOpen, Award, TrendingUp, AlertTriangle,
  Search, Download, FileText, ChevronUp, ChevronDown,
  Loader2, BarChart3, Clock, CheckCircle2, XCircle,
  ArrowRight, ShieldAlert, Calendar,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const OrgReporting = () => {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerReport, setLearnerReport] = useState(null);
  const [learnerLoading, setLearnerLoading] = useState(false);

  useEffect(() => { fetchOverview(); }, []);
  useEffect(() => { if (tab === 'team') fetchTeam(); }, [tab, search, roleFilter, sortBy, sortDir, page]);
  useEffect(() => { if (tab === 'compliance') fetchCompliance(); }, [tab]);

  const fetchOverview = async () => {
    try {
      const res = await api.get('/organisations/reporting/overview');
      setOverview(res.data);
    } catch { toast.error('Failed to load overview'); }
    finally { setLoading(false); }
  };

  const fetchTeam = async () => {
    try {
      const params = new URLSearchParams({
        page: String(page), per_page: '15', sort_by: sortBy, sort_dir: sortDir,
      });
      if (search) params.set('search', search);
      if (roleFilter) params.set('role_filter', roleFilter);
      const res = await api.get(`/organisations/reporting/team?${params}`);
      setTeamData(res.data);
    } catch { toast.error('Failed to load team data'); }
  };

  const fetchCompliance = async () => {
    try {
      const res = await api.get('/organisations/reporting/compliance');
      setCompliance(res.data);
    } catch { toast.error('Failed to load compliance data'); }
  };

  const openLearnerReport = async (learnerId) => {
    setSelectedLearner(learnerId);
    setLearnerLoading(true);
    try {
      const res = await api.get(`/organisations/reporting/learner/${learnerId}`);
      setLearnerReport(res.data);
    } catch { toast.error('Failed to load learner report'); }
    finally { setLearnerLoading(false); }
  };

  const exportCSV = async () => {
    try {
      const res = await api.get('/organisations/reporting/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'team_report.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  const exportLearnerPDF = () => {
    if (!learnerReport) return;
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then(({ default: autoTable }) => {
        const doc = new jsPDF();
        const lr = learnerReport.learner;

        doc.setFontSize(18);
        doc.setTextColor(30, 58, 95);
        doc.text('Common Impact E-learning', 14, 20);
        doc.setFontSize(12);
        doc.setTextColor(100);
        doc.text('Individual Learner Report', 14, 28);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 34);

        doc.setFontSize(14);
        doc.setTextColor(30, 58, 95);
        doc.text(`${lr.name}`, 14, 48);
        doc.setFontSize(10);
        doc.setTextColor(80);
        doc.text(`Email: ${lr.email}  |  Role: ${lr.team_role}  |  Joined: ${(lr.joined || '').slice(0, 10)}`, 14, 55);
        doc.text(`Courses: ${learnerReport.total_enrolled} enrolled, ${learnerReport.total_completed} completed`, 14, 61);

        const rows = learnerReport.courses.map(c => [
          c.course_title,
          `${Math.round(c.progress_pct)}%`,
          c.status,
          c.quiz_scores.length > 0 ? `${c.quiz_scores[c.quiz_scores.length - 1].score}%` : '-',
          c.completed_at ? c.completed_at.slice(0, 10) : '-',
          c.certificate ? c.certificate.number : '-',
        ]);

        autoTable(doc, {
          startY: 68,
          head: [['Course', 'Progress', 'Status', 'Quiz Score', 'Completed', 'Certificate']],
          body: rows,
          headStyles: { fillColor: [30, 58, 95] },
          styles: { fontSize: 9 },
        });

        doc.save(`${lr.name.replace(/\s+/g, '_')}_report.pdf`);
        toast.success('PDF exported');
      });
    });
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['org_admin', 'super_admin']}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['org_admin', 'super_admin']}>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="org-reporting">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#2a3e5d]">Training Reports</h1>
            <p className="text-sm text-slate-500 mt-1">Monitor compliance, track progress, and export reports for CQC</p>
          </div>
          <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Team Members</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <OverviewTab overview={overview} />
          </TabsContent>

          {/* TEAM TAB */}
          <TabsContent value="team">
            <TeamTab
              teamData={teamData}
              search={search} setSearch={setSearch}
              roleFilter={roleFilter} setRoleFilter={setRoleFilter}
              sortBy={sortBy} setSortBy={setSortBy}
              sortDir={sortDir} setSortDir={setSortDir}
              page={page} setPage={setPage}
              onViewLearner={openLearnerReport}
            />
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance">
            <ComplianceTab compliance={compliance} />
          </TabsContent>
        </Tabs>

        {/* Learner Report Dialog */}
        <Dialog open={!!selectedLearner} onOpenChange={(open) => { if (!open) { setSelectedLearner(null); setLearnerReport(null); } }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Individual Learner Report</DialogTitle>
            </DialogHeader>
            {learnerLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
            ) : learnerReport ? (
              <LearnerReportView report={learnerReport} onExportPDF={exportLearnerPDF} />
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// ============================================================
// OVERVIEW TAB
// ============================================================
const OverviewTab = ({ overview }) => {
  if (!overview) return null;
  const metrics = [
    { label: 'Team Members', value: overview.total_members, icon: Users, color: 'bg-[#f8fafc] text-[#2a3e5d]' },
    { label: 'Completed (30d)', value: overview.courses_completed_30d, icon: Award, color: 'bg-green-50 text-green-700' },
    { label: 'Avg Completion', value: `${overview.avg_completion_rate}%`, icon: TrendingUp, color: 'bg-[#f8fafc] text-purple-700' },
    { label: 'Quiz Pass Rate', value: `${overview.avg_quiz_pass_rate}%`, icon: CheckCircle2, color: 'bg-amber-50 text-amber-700' },
  ];

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m, i) => {
          const Icon = m.icon;
          return (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{m.value}</p>
                    <p className="text-xs text-slate-500">{m.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Alerts */}
      {overview.overdue_alerts?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Compliance Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {overview.overdue_alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg text-sm">
                <span className="text-slate-800">{a.course_title}</span>
                <Badge variant="destructive" className="text-xs">
                  {a.not_completed_count}/{a.total_members} incomplete
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Completion Trend (6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={overview.completion_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="completions" stroke="#20acf6" strokeWidth={2.5} dot={{ r: 4, fill: '#20acf6' }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Course Completions</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={overview.course_breakdown || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="course" type="category" width={140} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="completions" fill="#02d1d4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============================================================
// TEAM TAB
// ============================================================
const TeamTab = ({
  teamData, search, setSearch, roleFilter, setRoleFilter,
  sortBy, setSortBy, sortDir, setSortDir, page, setPage,
  onViewLearner,
}) => {
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email..."
            className="pl-10"
            data-testid="search-team"
          />
        </div>
        <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="role-filter">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-slate-50">
            <tr>
              {[
                { key: 'name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'team_role', label: 'Role' },
                { key: 'courses_enrolled', label: 'Enrolled' },
                { key: 'courses_completed', label: 'Completed' },
                { key: 'avg_quiz_score', label: 'Avg Score' },
                { key: 'last_active', label: 'Last Active' },
              ].map(col => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:text-[#2a3e5d] select-none"
                  onClick={() => toggleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label} <SortIcon col={col.key} />
                  </span>
                </th>
              ))}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {teamData?.members?.length > 0 ? teamData.members.map((m) => (
              <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{m.name}</td>
                <td className="px-4 py-3 text-slate-600">{m.email}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="text-xs capitalize">{m.team_role}</Badge>
                </td>
                <td className="px-4 py-3 text-center">{m.courses_enrolled}</td>
                <td className="px-4 py-3 text-center">{m.courses_completed}</td>
                <td className="px-4 py-3 text-center">
                  <span className={m.avg_quiz_score >= 85 ? 'text-green-600 font-medium' : m.avg_quiz_score > 0 ? 'text-amber-600' : 'text-slate-400'}>
                    {m.avg_quiz_score > 0 ? `${m.avg_quiz_score}%` : '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {m.last_active ? new Date(m.last_active).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => onViewLearner(m.id)}
                    data-testid={`view-learner-${m.id}`}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                  No team members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {teamData && teamData.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Page {teamData.page} of {teamData.total_pages} ({teamData.total} members)
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={page >= teamData.total_pages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPLIANCE TAB
// ============================================================
const ComplianceTab = ({ compliance }) => {
  if (!compliance) return (
    <div className="text-center py-12 text-slate-500">
      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
      Loading compliance data...
    </div>
  );

  return (
    <div className="space-y-6" data-testid="compliance-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#2a3e5d]">{compliance.compliance_rate}%</p>
            <p className="text-xs text-slate-500">Compliance Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{compliance.total_completed}</p>
            <p className="text-xs text-slate-500">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{compliance.total_overdue}</p>
            <p className="text-xs text-slate-500">Overdue</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-600">{compliance.total_required}</p>
            <p className="text-xs text-slate-500">Total Required</p>
          </CardContent>
        </Card>
      </div>

      {compliance.courses?.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldAlert className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No mandatory courses set</p>
            <p className="text-sm text-slate-400 mt-1">
              Mark courses as mandatory in the Course Builder settings to track compliance.
            </p>
          </CardContent>
        </Card>
      )}

      {compliance.courses?.map((course) => (
        <Card key={course.course_id}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{course.course_title}</CardTitle>
              {course.category && <Badge variant="outline" className="text-xs">{course.category}</Badge>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {course.members.map((m) => (
                <div key={m.user_id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 text-sm">
                  <div className="flex items-center gap-3">
                    {m.completed && !m.is_expired ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : m.is_expired ? (
                      <Clock className="h-4 w-4 text-amber-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span className="font-medium text-slate-800">{m.name}</span>
                    <Badge variant="outline" className="text-xs capitalize">{m.team_role || 'staff'}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.completed && !m.is_expired && (
                      <span className="text-xs text-green-600">
                        Completed {m.completed_at ? new Date(m.completed_at).toLocaleDateString() : ''}
                      </span>
                    )}
                    {m.is_expired && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                    {!m.completed && <Badge variant="destructive" className="text-xs">Overdue</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============================================================
// LEARNER REPORT VIEW (Dialog)
// ============================================================
const LearnerReportView = ({ report, onExportPDF }) => {
  const lr = report.learner;

  return (
    <div className="space-y-6" data-testid="learner-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{lr.name}</h3>
          <p className="text-sm text-slate-500">{lr.email} &middot; {lr.team_role} &middot; Joined {(lr.joined || '').slice(0, 10)}</p>
        </div>
        <Button onClick={onExportPDF} variant="outline" size="sm" data-testid="export-pdf-btn">
          <FileText className="h-4 w-4 mr-2" /> Export PDF
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#f8fafc] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-[#2a3e5d]">{report.total_enrolled}</p>
          <p className="text-xs text-slate-500">Enrolled</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{report.total_completed}</p>
          <p className="text-xs text-slate-500">Completed</p>
        </div>
        <div className="bg-[#f8fafc] rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">{report.total_time_minutes}m</p>
          <p className="text-xs text-slate-500">Time Spent</p>
        </div>
      </div>

      {/* Course list */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-3">Courses</h4>
        <div className="space-y-3">
          {report.courses.map((c) => (
            <div key={c.course_id} className="p-4 border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 text-sm">{c.course_title}</span>
                  {c.is_mandatory && <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>}
                </div>
                <Badge className={
                  c.status === 'completed' ? 'bg-green-100 text-green-700' :
                  c.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-700'
                } variant="outline">
                  {c.status === 'completed' ? 'Completed' : c.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                </Badge>
              </div>
              <Progress value={c.progress_pct} className="h-2 mb-2" />
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{Math.round(c.progress_pct)}% complete</span>
                <div className="flex gap-3">
                  {c.quiz_scores.length > 0 && (
                    <span>Quiz: {c.quiz_scores[c.quiz_scores.length - 1].score}%
                      {c.quiz_scores[c.quiz_scores.length - 1].passed ? ' (Pass)' : ' (Fail)'}
                    </span>
                  )}
                  {c.certificate && (
                    <span className="text-green-600">Cert: {c.certificate.number}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {report.courses.length === 0 && (
            <p className="text-sm text-slate-400 italic text-center py-4">No courses enrolled</p>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      {report.timeline?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Activity Timeline</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {report.timeline.slice(0, 20).map((t, i) => (
              <div key={i} className="flex items-center gap-3 text-sm py-1">
                <div className={`w-2 h-2 rounded-full ${
                  t.type === 'completed' ? 'bg-green-500' :
                  t.type === 'quiz' ? (t.passed ? 'bg-blue-500' : 'bg-amber-500') :
                  'bg-slate-400'
                }`} />
                <span className="text-slate-600 flex-1">
                  {t.type === 'enrolled' && `Enrolled in ${t.course}`}
                  {t.type === 'completed' && `Completed ${t.course}`}
                  {t.type === 'quiz' && `Quiz: ${t.score}% (${t.passed ? 'Pass' : 'Fail'}) - ${t.course}`}
                </span>
                <span className="text-xs text-slate-400">
                  {t.date ? new Date(t.date).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgReporting;
